// src/hooks/useDeliveries.ts - Hooks pour la gestion des approvisionnements avec impact stock automatique

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
  increment,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { matchesSearchInFields } from '../utils/searchUtils';
import { 
  Delivery, 
  DeliveryFilters, 
  CreateDeliveryData,
  DeliveryStatus,
  DeliveryItem, 
  Supplier
} from '../types/suppliers';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { calculateDeliveryTotalsWithTax } from '../utils/deliveryTaxCalculations';
import { getBusinessConfiguration } from '../types/business';
import { useDefaultReceivingSite } from './useSites';
import { getMissingDefaultSiteError } from '../utils/siteDefaults';
import { getProductStockAtSite } from '../utils/productStock';
import { useCreateStockNotification } from './useNotifications';
import { getStockAlertThresholdForSite } from '../utils/stockAlerts';

/** Retire les champs à valeur undefined d'un objet (et des tableaux d'objets) pour Firestore. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      result[key] = value.map((item) =>
        item !== null && typeof item === 'object' && !(item instanceof Date) && typeof (item as { toDate?: () => Date }).toDate !== 'function'
          ? stripUndefined(item as Record<string, unknown>)
          : item
      );
    } else if (value !== null && typeof value === 'object' && !(value instanceof Date) && typeof (value as { toDate?: () => Date }).toDate !== 'function') {
      result[key] = stripUndefined(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }
  return result;
}

// ===== HOOKS POUR LES LIVRAISONS =====

export const useDeliveries = (filters?: DeliveryFilters) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['deliveries', businessId, filters],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        let q;

        // Stratégie de requête selon les filtres
        if (filters?.supplierId && filters?.dateRange) {
          q = query(
            collection(db, COLLECTIONS.deliveries),
            where('businessId', '==', businessId),
            where('supplierId', '==', filters.supplierId),
            where('deliveryDate', '>=', Timestamp.fromDate(filters.dateRange[0])),
            where('deliveryDate', '<=', Timestamp.fromDate(filters.dateRange[1])),
            orderBy('deliveryDate', 'desc')
          );
        } else if (filters?.supplierId) {
          q = query(
            collection(db, COLLECTIONS.deliveries),
            where('businessId', '==', businessId),
            where('supplierId', '==', filters.supplierId),
            orderBy('deliveryDate', 'desc'),
            limit(50)
          );
        } else if (filters?.dateRange) {
          q = query(
            collection(db, COLLECTIONS.deliveries),
            where('businessId', '==', businessId),
            where('deliveryDate', '>=', Timestamp.fromDate(filters.dateRange[0])),
            where('deliveryDate', '<=', Timestamp.fromDate(filters.dateRange[1])),
            orderBy('deliveryDate', 'desc')
          );
        } else {
          q = query(
            collection(db, COLLECTIONS.deliveries),
            where('businessId', '==', businessId),
            orderBy('deliveryDate', 'desc'),
            limit(100)
          );
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useDeliveries - Main query');
          throw error;
        });
        
        let deliveries = snapshot.docs.map(doc => {
          const data = doc.data();
          
          // 🔍 DEBUG : Vérifier les items dans Firestore
          console.log(`🔍 [useDeliveries] Delivery ${doc.id}:`, {
            hasItems: !!data.items,
            itemsType: Array.isArray(data.items) ? 'array' : typeof data.items,
            itemsLength: Array.isArray(data.items) ? data.items.length : 'N/A',
            itemsRaw: data.items,
            totalQuantity: data.totalQuantity,
            totalAmount: data.totalAmount,
          });
          
          // ✅ CORRIGÉ : S'assurer que les items sont correctement mappés
          let items: DeliveryItem[] = [];
          if (data.items && Array.isArray(data.items)) {
            items = data.items.map((item: any) => {
              console.log(`  📦 Item:`, {
                productId: item.productId,
                productName: item.productName,
                quantity: item.quantity,
                unitCost: item.unitCost,
                totalCost: item.totalCost,
              });
              
              return {
                productId: item.productId || '',
                productName: item.productName || '',
                quantity: item.quantity || 0,
                unitCost: item.unitCost || 0,
                totalCost: item.totalCost || (item.quantity || 0) * (item.unitCost || 0),
                previousStock: item.previousStock,
                newStock: item.newStock,
                // Champs TVA
                taxRateId: item.taxRateId,
                taxRateName: item.taxRateName,
                taxRate: item.taxRate,
                unitCostExcludingTax: item.unitCostExcludingTax,
                unitCostIncludingTax: item.unitCostIncludingTax,
                taxAmount: item.taxAmount,
                totalTaxAmount: item.totalTaxAmount,
                totalCostExcludingTax: item.totalCostExcludingTax,
                totalCostIncludingTax: item.totalCostIncludingTax,
              };
            });
          } else {
            console.warn(`⚠️ [useDeliveries] Delivery ${doc.id} n'a pas d'items ou items n'est pas un tableau:`, data.items);
          }
          
          console.log(`✅ [useDeliveries] Delivery ${doc.id} mappée avec ${items.length} items`);
          
          return {
            id: doc.id,
            businessId: data.businessId,
            supplierId: data.supplierId,
            supplierName: data.supplierName,
            deliveryDate: data.deliveryDate?.toDate(),
            reference: data.reference,
            items: items,
            totalQuantity: data.totalQuantity || 0,
            totalAmount: data.totalAmount || 0,
            subtotalExcludingTax: data.subtotalExcludingTax,
            totalTaxAmount: data.totalTaxAmount,
            subtotalIncludingTax: data.subtotalIncludingTax,
            taxSummary: data.taxSummary,
            status: data.status,
            employeeId: data.employeeId,
            employeeName: data.employeeName,
            notes: data.notes,
            siteId: data.siteId,
            siteName: data.siteName,
            fromSiteId: data.fromSiteId,
            fromSiteName: data.fromSiteName,
            isInternalTransfer: data.isInternalTransfer || false,
            createdAt: data.createdAt?.toDate(),
            processedAt: data.processedAt?.toDate(),
          } as Delivery;
        });

        // Filtres côté client
        if (filters?.status) {
          deliveries = deliveries.filter(delivery => delivery.status === filters.status);
        }

        if (filters?.employeeId) {
          deliveries = deliveries.filter(delivery => delivery.employeeId === filters.employeeId);
        }

        if (filters?.searchTerm) {
          deliveries = deliveries.filter(delivery =>
            matchesSearchInFields(
              [
                delivery.supplierName,
                delivery.reference,
                delivery.notes,
                delivery.employeeName,
              ],
              filters.searchTerm!
            )
          );
        }

        return deliveries;
        
      } catch (error) {
        console.error('Erreur lors de la récupération des approvisionnements:', error);
        return [];
      }
    },
    enabled: !!businessId && (permissions?.canViewDeliveries ?? false),
  });
};

export const useDelivery = (deliveryId: string) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['deliveries', businessId, deliveryId],
    queryFn: async () => {
      if (!deliveryId || !businessId) return null;

      const docRef = doc(db, COLLECTIONS.deliveries, deliveryId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Approvisionnement introuvable');
      }

      const data = docSnap.data();
      
      if (data.businessId !== businessId) {
        throw new Error('Cet approvisionnement n\'appartient pas à votre business');
      }

      // 🔍 DEBUG : Vérifier les items dans Firestore
      console.log(`🔍 [useDelivery] Delivery ${deliveryId}:`, {
        hasItems: !!data.items,
        itemsType: Array.isArray(data.items) ? 'array' : typeof data.items,
        itemsLength: Array.isArray(data.items) ? data.items.length : 'N/A',
        itemsRaw: data.items,
      });

      // ✅ CORRIGÉ : S'assurer que les items sont correctement mappés
      let items: DeliveryItem[] = [];
      if (data.items && Array.isArray(data.items)) {
        items = data.items.map((item: any) => ({
          productId: item.productId || '',
          productName: item.productName || '',
          quantity: item.quantity || 0,
          unitCost: item.unitCost || 0,
          totalCost: item.totalCost || (item.quantity || 0) * (item.unitCost || 0),
          previousStock: item.previousStock,
          newStock: item.newStock,
          // Champs TVA
          taxRateId: item.taxRateId,
          taxRateName: item.taxRateName,
          taxRate: item.taxRate,
          unitCostExcludingTax: item.unitCostExcludingTax,
          unitCostIncludingTax: item.unitCostIncludingTax,
          taxAmount: item.taxAmount,
          totalTaxAmount: item.totalTaxAmount,
          totalCostExcludingTax: item.totalCostExcludingTax,
          totalCostIncludingTax: item.totalCostIncludingTax,
        }));
      } else {
        console.warn(`⚠️ [useDelivery] Delivery ${deliveryId} n'a pas d'items ou items n'est pas un tableau:`, data.items);
      }

      return {
        id: docSnap.id,
        businessId: data.businessId,
        supplierId: data.supplierId,
        supplierName: data.supplierName,
        deliveryDate: data.deliveryDate?.toDate(),
        reference: data.reference,
        items: items,
        totalQuantity: data.totalQuantity || 0,
        totalAmount: data.totalAmount || 0,
        subtotalExcludingTax: data.subtotalExcludingTax,
        totalTaxAmount: data.totalTaxAmount,
        subtotalIncludingTax: data.subtotalIncludingTax,
        taxSummary: data.taxSummary,
        status: data.status,
        employeeId: data.employeeId,
        employeeName: data.employeeName,
        notes: data.notes,
        siteId: data.siteId,
        siteName: data.siteName,
        createdAt: data.createdAt?.toDate(),
        processedAt: data.processedAt?.toDate(),
      } as Delivery;
    },
    enabled: !!deliveryId && !!businessId && (permissions?.canViewDeliveries ?? false),
  });
};

export const useAddDelivery = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;
  const defaultReceivingSite = useDefaultReceivingSite(businessId);
  const createStockNotification = useCreateStockNotification();
  
  return useMutation({
    mutationFn: async (deliveryData: CreateDeliveryData) => {
      if (!businessId || !userProfile) {
        throw new Error('Business ou utilisateur non défini');
      }

      if (!deliveryData.items || deliveryData.items.length === 0) {
        throw new Error('Un approvisionnement doit contenir au moins un produit');
      }

      // Vérifier que le fournisseur existe et appartient au business
      const supplierRef = doc(db, COLLECTIONS.suppliers, deliveryData.supplierId);
      const supplierSnap = await getDoc(supplierRef);
      
      if (!supplierSnap.exists()) {
        throw new Error('Fournisseur introuvable');
      }
      
      const supplierData = supplierSnap.data();
      if (supplierData.businessId !== businessId) {
        throw new Error('Ce fournisseur n\'appartient pas à votre business');
      }

      // Configuration business pour les calculs TVA
      const businessConfig = getBusinessConfiguration(currentBusiness);
      const hasTaxConfig = Boolean(businessConfig.tax?.rates?.length);
      
      console.log('🔍 [useAddDelivery] Configuration TVA:', {
        hasTaxConfig,
        taxRatesCount: businessConfig.tax?.rates?.length || 0,
      });

      // Vérifier que tous les produits existent et appartiennent au business
      const productValidations: Array<{
        item: typeof deliveryData.items[0];
        productData: any;
        previousStock: number;
      }> = [];
      
      for (const item of deliveryData.items) {
        const productRef = doc(db, COLLECTIONS.products, item.productId);
        const productSnap = await getDoc(productRef);
        
        if (!productSnap.exists()) {
          throw new Error(`Produit "${item.productName}" introuvable`);
        }
        
        const productData = productSnap.data();
        if (productData.businessId !== businessId) {
          throw new Error(`Le produit "${item.productName}" n'appartient pas à votre business`);
        }
        
        productValidations.push({
          item,
          productData,
          previousStock: productData.currentStock || 0,
        });
      }

      // Créer les items enrichis avec calculs TVA si configuré
      let enrichedItems: DeliveryItem[] = productValidations.map(validation => ({
        productId: validation.item.productId,
        productName: validation.item.productName,
        quantity: validation.item.quantity,
        unitCost: validation.item.unitCost,
        totalCost: validation.item.quantity * validation.item.unitCost,
        previousStock: validation.previousStock,
        newStock: validation.previousStock + validation.item.quantity,
      }));

      // Si on a une configuration TVA, enrichir les items
      if (hasTaxConfig) {
        const tempDelivery: Delivery = {
          id: 'temp',
          businessId,
          supplierId: deliveryData.supplierId,
          supplierName: supplierData.name,
          deliveryDate: deliveryData.deliveryDate,
          reference: deliveryData.reference,
          items: enrichedItems,
          totalQuantity: enrichedItems.reduce((sum, item) => sum + item.quantity, 0),
          totalAmount: enrichedItems.reduce((sum, item) => sum + item.totalCost, 0),
          status: 'received' as DeliveryStatus,
          employeeId: userProfile.id,
          employeeName: userProfile.name,
          notes: deliveryData.notes,
          createdAt: new Date(),
        };
      
        // Convertir supplierData en objet Supplier typé
        const supplier: Supplier = {
          id: deliveryData.supplierId,
          businessId: supplierData.businessId,
          name: supplierData.name,
          contactPerson: supplierData.contactPerson,
          phone: supplierData.phone,
          email: supplierData.email,
          address: supplierData.address,
          paymentTerms: supplierData.paymentTerms,
          notes: supplierData.notes,
          pricesIncludeTax: supplierData.pricesIncludeTax,
          taxNumber: supplierData.taxNumber,
          createdAt: supplierData.createdAt?.toDate(),
          updatedAt: supplierData.updatedAt?.toDate(),
        };
      
        // Récupérer les produits complets pour les calculs TVA
        const products = productValidations.map(v => ({
          id: v.item.productId,
          name: v.productData.name,
          taxRateId: v.productData.taxRateId,
          costPrice: v.productData.costPrice,
          sellingPrice: v.productData.sellingPrice,
          priceIncludesTax: v.productData.priceIncludesTax,
        }));
        
        // Enrichir avec la TVA
        const enrichedDelivery = calculateDeliveryTotalsWithTax(
          tempDelivery,
          supplier,
          products as any,
          currentBusiness
        );
      
        enrichedItems = enrichedDelivery.items;
        
        console.log('✅ [useAddDelivery] Items enrichis avec TVA:', {
          itemsCount: enrichedItems.length,
          firstItemTax: enrichedItems[0]?.totalTaxAmount,
          totalTaxCalculated: enrichedItems.reduce((sum, item) => sum + (item.totalTaxAmount || 0), 0),
        });
      }

      const totalQuantity = enrichedItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalAmount = enrichedItems.reduce((sum, item) => sum + item.totalCost, 0);

      // Décision produit: ne plus recalculer automatiquement les coûts à la livraison.
      // Les mises à jour de prix se font désormais manuellement à la demande client.
      const recalculateCost = false;
      const effectiveSiteId = deliveryData.siteId || defaultReceivingSite?.id;
      const effectiveSiteName = deliveryData.siteName || defaultReceivingSite?.name;
      if (!effectiveSiteId || !effectiveSiteName) {
        throw new Error(getMissingDefaultSiteError(currentBusiness));
      }

      // Utiliser une transaction batch pour garantir la cohérence
      const batch = writeBatch(db);

      // 1. Créer l'approvisionnement avec totaux TVA si configuré
      const deliveryRef = doc(collection(db, COLLECTIONS.deliveries));
      const newDelivery: any = {
        businessId,
        supplierId: deliveryData.supplierId,
        supplierName: supplierData.name,
        deliveryDate: Timestamp.fromDate(deliveryData.deliveryDate),
        reference: deliveryData.reference?.trim() || null,
        attachments: deliveryData.attachments || [],
        items: enrichedItems,
        totalQuantity,
        totalAmount,
        status: 'received' as DeliveryStatus,
        employeeId: userProfile.id,
        employeeName: userProfile.name,
        notes: deliveryData.notes?.trim() || null,
        siteId: effectiveSiteId,
        siteName: effectiveSiteName,
        createdAt: Timestamp.now(),
        processedAt: Timestamp.now(),
      };

      // Ajouter les totaux TVA si configuré
      if (hasTaxConfig) {
        const subtotalExcludingTax = enrichedItems.reduce((sum, item) => sum + (item.totalCostExcludingTax || item.totalCost), 0);
        const totalTaxAmount = enrichedItems.reduce((sum, item) => sum + (item.totalTaxAmount || 0), 0);
        const subtotalIncludingTax = enrichedItems.reduce((sum, item) => sum + (item.totalCostIncludingTax || item.totalCost), 0);

        newDelivery.subtotalExcludingTax = Math.round(subtotalExcludingTax * 100) / 100;
        newDelivery.totalTaxAmount = Math.round(totalTaxAmount * 100) / 100;
        newDelivery.subtotalIncludingTax = Math.round(subtotalIncludingTax * 100) / 100;
        
        console.log('💰 [useAddDelivery] Totaux TVA ajoutés au document:', {
          subtotalExcludingTax: newDelivery.subtotalExcludingTax,
          totalTaxAmount: newDelivery.totalTaxAmount,
          subtotalIncludingTax: newDelivery.subtotalIncludingTax,
        });
      } else {
        console.log('⚠️ [useAddDelivery] Pas de configuration TVA, totaux TVA non calculés');
      }

      // Firestore n'accepte pas les valeurs undefined : on les retire avant l'écriture
      batch.set(deliveryRef, stripUndefined(newDelivery) as Record<string, unknown>);

      // 2. Mettre à jour les stocks et informations fournisseur des produits
      const notificationPayloads: Array<{
        productId: string;
        productName: string;
        currentStock: number;
        minStockAlert: number;
        siteId?: string;
        siteName?: string;
      }> = [];
      for (let i = 0; i < enrichedItems.length; i++) {
        const item = enrichedItems[i];
        const validation = productValidations[i];
        const productRef = doc(db, COLLECTIONS.products, item.productId);
        
        // ✅ MODIFIÉ : Calculer le nouveau coût moyen pondéré sur les prix HT
        const currentStock = item.previousStock || 0;
        const currentCostHT = validation.productData.averageCost || validation.productData.costPrice || 0;
        const newQuantity = item.quantity;
        
        // Utiliser le coût HT pour le calcul du coût moyen
        const newUnitCostHT = item.unitCostExcludingTax || item.unitCost;
        
        const totalValueHT = (currentStock * currentCostHT) + (newQuantity * newUnitCostHT);
        const totalQuantityAfter = currentStock + newQuantity;
        const averageCostHT = totalQuantityAfter > 0 ? totalValueHT / totalQuantityAfter : newUnitCostHT;

        const productUpdate: Record<string, unknown> = {
          currentStock: increment(item.quantity),
          lastSupplierPrice: item.unitCost,
          lastDeliveryDate: Timestamp.fromDate(deliveryData.deliveryDate),
          preferredSupplierId: deliveryData.supplierId,
          preferredSupplierName: supplierData.name,
          updatedAt: Timestamp.now(),
        };
        if (recalculateCost) {
          productUpdate.averageCost = averageCostHT;
          productUpdate.costPrice = newUnitCostHT;
        }
        if (effectiveSiteId) {
          const existingBySite = (validation.productData.stockBySite as Record<string, number>) || {};
          const prevAtSite = getProductStockAtSite(
            {
              currentStock: validation.productData.currentStock,
              stockBySite: existingBySite,
            },
            effectiveSiteId
          );
          productUpdate.stockBySite = {
            ...existingBySite,
            [effectiveSiteId]: prevAtSite + item.quantity,
          };
          notificationPayloads.push({
            productId: item.productId,
            productName: item.productName,
            currentStock: Math.max(0, prevAtSite + item.quantity),
            minStockAlert: getStockAlertThresholdForSite(validation.productData, effectiveSiteId),
            siteId: effectiveSiteId,
            siteName: effectiveSiteName,
          });
        }
        batch.update(productRef, productUpdate as any);

        // 3. Créer le mouvement de stock correspondant
        const movementRef = doc(collection(db, COLLECTIONS.stockMovements));
        const stockMovement: any = {
          businessId,
          productId: item.productId,
          productName: item.productName,
          type: 'entry',
          quantity: item.quantity,
          unitCost: item.unitCost,  // Prix original saisi
          totalCost: item.totalCost,
          reason: 'Approvisionnement fournisseur',
          reference: deliveryData.reference || `APPRO-${deliveryRef.id}`,
          date: Timestamp.fromDate(deliveryData.deliveryDate),
          employeeId: userProfile.id,
          employeeName: userProfile.name,
          notes: `Approvisionnement de ${supplierData.name}`,
          supplierId: deliveryData.supplierId,
          supplierName: supplierData.name,
          deliveryId: deliveryRef.id,
          createdAt: Timestamp.now(),
          siteId: effectiveSiteId,
          siteName: effectiveSiteName,
        };

        // Ajouter les champs TVA au mouvement si configuré
        if (hasTaxConfig && item.taxRateId) {
          stockMovement.taxRateId = item.taxRateId;
          stockMovement.taxRateName = item.taxRateName;
          stockMovement.taxRate = item.taxRate;
          stockMovement.unitCostExcludingTax = item.unitCostExcludingTax;
          stockMovement.unitCostIncludingTax = item.unitCostIncludingTax;
          stockMovement.taxAmount = item.taxAmount;
          stockMovement.totalTaxAmount = item.totalTaxAmount;
          stockMovement.totalCostExcludingTax = item.totalCostExcludingTax;
          stockMovement.totalCostIncludingTax = item.totalCostIncludingTax;
        }

        // Firestore n'accepte pas les valeurs undefined
        batch.set(movementRef, stripUndefined(stockMovement) as Record<string, unknown>);
      }

      await batch.commit();
      await Promise.all(
        notificationPayloads.map((payload) =>
          createStockNotification.mutateAsync(payload).catch((error) => {
            console.warn('Erreur lors de la synchronisation de la notification de stock:', error);
          })
        )
      );
      
      return deliveryRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries', businessId] });
      queryClient.invalidateQueries({ queryKey: ['suppliers', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
    },
  });
};

export const useUpdateDeliveryStatus = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ 
      deliveryId, 
      status, 
      notes 
    }: { 
      deliveryId: string; 
      status: DeliveryStatus; 
      notes?: string; 
    }) => {
      if (!businessId) {
        throw new Error('Business non défini');
      }

      const deliveryRef = doc(db, COLLECTIONS.deliveries, deliveryId);
      const deliverySnap = await getDoc(deliveryRef);
      
      if (!deliverySnap.exists()) {
        throw new Error('Approvisionnement introuvable');
      }
      
      const delivery = deliverySnap.data();
      
      if (delivery.businessId !== businessId) {
        throw new Error('Cet approvisionnement n\'appartient pas à votre business');
      }

      const updateData: any = {
        status,
        updatedAt: Timestamp.now(),
      };

      if (notes?.trim()) {
        updateData.notes = notes.trim();
      }

      // Si on passe de 'pending' à 'received', marquer comme traité
      if (delivery.status === 'pending' && status === 'received') {
        updateData.processedAt = Timestamp.now();
      }

      await updateDoc(deliveryRef, updateData);
      
      return { deliveryId, status };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries', businessId] });
    },
  });
};

// Hook pour les approvisionnements par fournisseur
export const useDeliveriesBySupplier = (supplierId: string, limitCount?: number) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['deliveries', 'bySupplier', businessId, supplierId, limitCount],
    queryFn: async () => {
      if (!supplierId || !businessId) {
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.deliveries),
          where('businessId', '==', businessId),
          where('supplierId', '==', supplierId),
          orderBy('deliveryDate', 'desc')
        );

        if (limitCount) {
          q = query(q, limit(limitCount));
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useDeliveries - Main query');
          throw error;
        });
        
        return snapshot.docs.map(doc => {
          const data = doc.data();
          
          // ✅ CORRIGÉ : S'assurer que les items sont correctement mappés
          let items: DeliveryItem[] = [];
          if (data.items && Array.isArray(data.items)) {
            items = data.items.map((item: any) => ({
              productId: item.productId || '',
              productName: item.productName || '',
              quantity: item.quantity || 0,
              unitCost: item.unitCost || 0,
              totalCost: item.totalCost || (item.quantity || 0) * (item.unitCost || 0),
              previousStock: item.previousStock,
              newStock: item.newStock,
              // Champs TVA
              taxRateId: item.taxRateId,
              taxRateName: item.taxRateName,
              taxRate: item.taxRate,
              unitCostExcludingTax: item.unitCostExcludingTax,
              unitCostIncludingTax: item.unitCostIncludingTax,
              taxAmount: item.taxAmount,
              totalTaxAmount: item.totalTaxAmount,
              totalCostExcludingTax: item.totalCostExcludingTax,
              totalCostIncludingTax: item.totalCostIncludingTax,
            }));
          }
          
          return {
            id: doc.id,
            businessId: data.businessId,
            supplierId: data.supplierId,
            supplierName: data.supplierName,
            deliveryDate: data.deliveryDate?.toDate(),
            reference: data.reference,
            items: items,
            totalQuantity: data.totalQuantity || 0,
            totalAmount: data.totalAmount || 0,
            subtotalExcludingTax: data.subtotalExcludingTax,
            totalTaxAmount: data.totalTaxAmount,
            subtotalIncludingTax: data.subtotalIncludingTax,
            taxSummary: data.taxSummary,
            status: data.status,
            employeeId: data.employeeId,
            employeeName: data.employeeName,
            notes: data.notes,
            siteId: data.siteId,
            siteName: data.siteName,
            createdAt: data.createdAt?.toDate(),
            processedAt: data.processedAt?.toDate(),
          } as Delivery;
        });
        
      } catch (error) {
        console.error('Erreur lors de la récupération des approvisionnements du fournisseur:', error);
        return [];
      }
    },
    enabled: !!supplierId && !!businessId && (permissions?.canViewDeliveries ?? false),
  });
};

// Hook pour les statistiques des approvisionnements
// Les filtres (période, fournisseur, site, etc.) doivent impacter les stats.
export const useDeliveriesStats = (filters?: DeliveryFilters) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['deliveries', 'stats', businessId, filters],
    queryFn: async () => {
      if (!businessId) {
        return {
          totalDeliveries: 0,
          totalAmount: 0,
          totalQuantity: 0,
          totalTaxAmount: 0,
          averageDeliveryValue: 0,
          deliveriesByStatus: {
            pending: 0,
            received: 0,
            cancelled: 0,
          } as Record<DeliveryStatus, number>,
        };
      }

      try {
        let q: any = query(
          collection(db, COLLECTIONS.deliveries),
          where('businessId', '==', businessId),
          orderBy('deliveryDate', 'desc')
        );

        if (filters?.dateRange) {
          q = query(
            q,
            where('deliveryDate', '>=', Timestamp.fromDate(filters.dateRange[0])),
            where('deliveryDate', '<=', Timestamp.fromDate(filters.dateRange[1]))
          );
        }

        if (filters?.supplierId) {
          q = query(
            q,
            where('supplierId', '==', filters.supplierId)
          );
        }

        if (filters?.siteId) {
          q = query(
            q,
            where('siteId', '==', filters.siteId)
          );
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useDeliveries - Main query');
          throw error;
        });
        
        let totalAmount = 0;
        let totalQuantity = 0;
        let totalTaxAmount = 0;
        const deliveriesByStatus = {
          pending: 0,
          received: 0,
          cancelled: 0,
        } as Record<DeliveryStatus, number>;

        snapshot.docs.forEach(doc => {
          const data = doc.data() as any;
          totalAmount += data.totalAmount || 0;
          totalQuantity += data.totalQuantity || 0;
          totalTaxAmount += data.totalTaxAmount || 0;
          
          const status = data.status as DeliveryStatus;
          if (deliveriesByStatus.hasOwnProperty(status)) {
            deliveriesByStatus[status]++;
          }
        });

        const totalDeliveries = snapshot.size;
        const averageDeliveryValue = totalDeliveries > 0 ? totalAmount / totalDeliveries : 0;

        return {
          totalDeliveries,
          totalAmount,
          totalQuantity,
          totalTaxAmount,
          averageDeliveryValue,
          deliveriesByStatus,
        };
        
      } catch (error) {
        console.error('Erreur lors du calcul des statistiques d\'approvisionnements:', error);
        return {
          totalDeliveries: 0,
          totalAmount: 0,
          totalQuantity: 0,
          totalTaxAmount: 0,
          averageDeliveryValue: 0,
          deliveriesByStatus: {
            pending: 0,
            received: 0,
            cancelled: 0,
          } as Record<DeliveryStatus, number>,
        };
      }
    },
    enabled: !!businessId && (permissions?.canViewDeliveries ?? false),
  });
};

// Hook pour vérifier si un fournisseur a des approvisionnements
export const useSupplierHasDeliveries = (supplierId?: string) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['supplierHasDeliveries', businessId, supplierId],
    queryFn: async () => {
      if (!businessId || !supplierId) {
        return false;
      }

      try {
        const q = query(
          collection(db, COLLECTIONS.deliveries),
          where('businessId', '==', businessId),
          where('supplierId', '==', supplierId),
          limit(1) // On a juste besoin de savoir s'il y en a au moins une
        );
        
        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useDeliveries - Main query');
          throw error;
        });
        return !snapshot.empty;
      } catch (error) {
        console.error('Erreur lors de la vérification des approvisionnements du fournisseur:', error);
        return false;
      }
    },
    enabled: !!businessId && !!supplierId && (permissions?.canViewDeliveries ?? false),
  });
};

// Hook pour annuler un approvisionnement avec validation de stock
export const useCancelDelivery = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
const businessId = currentBusiness?.id;
  const createStockNotification = useCreateStockNotification();

  return useMutation({
    mutationFn: async (deliveryId: string) => {
      if (!businessId || !userProfile) {
        throw new Error('Business ou utilisateur non défini');
      }

      // 1. Récupérer l'approvisionnement
      const deliveryRef = doc(db, COLLECTIONS.deliveries, deliveryId);
      const deliverySnap = await getDoc(deliveryRef);

      if (!deliverySnap.exists()) {
        throw new Error('Approvisionnement introuvable');
      }

      const delivery = deliverySnap.data();

      if (delivery.businessId !== businessId) {
        throw new Error('Cet approvisionnement n\'appartient pas à votre business');
      }

      if (delivery.status !== 'received') {
        throw new Error('Seuls les approvisionnements reçus peuvent être annulés');
      }

      if (delivery.status === 'cancelled') {
        throw new Error('Cet approvisionnement est déjà annulé');
      }

      // 2. Vérifier le stock pour CHAQUE produit
      const stockValidations: Array<{
        productId: string;
        productName: string;
        quantityToRemove: number;
        currentStock: number;
        currentStockAtSite: number;
        canCancel: boolean;
        productData: any;
      }> = [];

      for (const item of delivery.items) {
        const productRef = doc(db, COLLECTIONS.products, item.productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
          throw new Error(`Produit "${item.productName}" introuvable (ID: ${item.productId}). Impossible d'annuler l'approvisionnement.`);
        }

        const productData = productSnap.data();
        if (productData.businessId !== businessId) {
          throw new Error(`Le produit "${item.productName}" n'appartient pas à votre institut. Impossible d'annuler l'approvisionnement.`);
        }
        const currentStock = Number(productData.currentStock || 0);
        const stockBySite = (productData.stockBySite as Record<string, number>) || {};
        const currentStockAtSite = getProductStockAtSite(
          { currentStock, stockBySite },
          delivery.siteId
        );
        const canCancel = currentStockAtSite >= item.quantity;

        stockValidations.push({
          productId: item.productId,
          productName: item.productName,
          quantityToRemove: item.quantity,
          currentStock,
          currentStockAtSite,
          canCancel,
          productData,
        });
      }

      // 3. Vérifier si TOUS les produits ont un stock suffisant
      const insufficientStock = stockValidations.filter(v => !v.canCancel);

      if (insufficientStock.length > 0) {
        const errorMessages = insufficientStock.map(
          v => `- ${v.productName}: stock du lieu ${delivery.siteName || 'sélectionné'} ${v.currentStockAtSite}, quantité à retirer ${v.quantityToRemove}`
        );
        throw new Error(
          `Impossible d'annuler : stock insuffisant pour ${insufficientStock.length} produit(s) :\n${errorMessages.join('\n')}`
        );
      }

      // Décision produit: ne plus recalculer automatiquement les coûts lors d'une annulation
      // d'approvisionnement afin de conserver des prix stables.
      const recalculateCost = false;

      // 4. Tout est OK → Annulation avec batch
      const batch = writeBatch(db);

      // Mettre à jour le status de l'approvisionnement
      batch.update(deliveryRef, {
        status: 'cancelled',
        cancelledAt: Timestamp.now(),
        cancelledBy: userProfile.id,
        updatedAt: Timestamp.now(),
      });

      // Pour chaque produit : retirer du stock et créer mouvement inverse
      const notificationPayloads: Array<{
        productId: string;
        productName: string;
        currentStock: number;
        minStockAlert: number;
        siteId?: string;
        siteName?: string;
      }> = [];
      for (let i = 0; i < delivery.items.length; i++) {
        const item = delivery.items[i];
        const validation = stockValidations[i];
        const productRef = doc(db, COLLECTIONS.products, item.productId);

        const currentStock = validation.currentStock;
        const currentCostHT = validation.productData.averageCost || validation.productData.costPrice || 0;
        const removedQuantity = item.quantity;
        const removedUnitCostHT = item.unitCostExcludingTax || item.unitCost;

        // Recalculer le coût moyen en RETIRANT la valeur
        const stockBySite = (validation.productData.stockBySite as Record<string, number>) || {};
        const currentAtSite = getProductStockAtSite(
          { currentStock, stockBySite },
          delivery.siteId
        );
        const newAtSite = Math.max(0, currentAtSite - removedQuantity);
        const updatedStockBySite: Record<string, number> | undefined = delivery.siteId
          ? {
              ...stockBySite,
              [delivery.siteId]: newAtSite,
            }
          : undefined;
        const newStock = updatedStockBySite
          ? Object.values(updatedStockBySite).reduce<number>(
              (sum, qty) => sum + (Number(qty) || 0),
              0
            )
          : Math.max(0, currentStock - removedQuantity);
        let newAverageCostHT = currentCostHT; // Par défaut, garder le coût actuel

        if (newStock > 0) {
          // Recalculer uniquement s'il reste du stock
          const totalValueBefore = currentStock * currentCostHT;
          const removedValue = removedQuantity * removedUnitCostHT;
          const totalValueAfter = totalValueBefore - removedValue;
          newAverageCostHT = totalValueAfter / newStock;
        }

        // Mettre à jour le produit
        const productUpdate: Record<string, unknown> = {
          currentStock: newStock,
          updatedAt: Timestamp.now(),
        };
        if (updatedStockBySite) {
          productUpdate.stockBySite = updatedStockBySite;
        }
        if (delivery.siteId) {
          notificationPayloads.push({
            productId: item.productId,
            productName: item.productName,
            currentStock: newAtSite,
            minStockAlert: getStockAlertThresholdForSite(validation.productData, delivery.siteId),
            siteId: delivery.siteId,
            siteName: delivery.siteName,
          });
        }
        if (recalculateCost) {
          productUpdate.averageCost = Math.max(0, newAverageCostHT);
        }
        batch.update(productRef, productUpdate as any);

        // Créer le mouvement de stock inverse (sortie)
        const movementRef = doc(collection(db, COLLECTIONS.stockMovements));
        const stockMovement: any = {
          businessId,
          productId: item.productId,
          productName: item.productName,
          type: 'exit',
          quantity: item.quantity,
          unitCost: item.unitCost,
          totalCost: item.totalCost,
          reason: 'Annulation approvisionnement',
          reference: delivery.reference || `APPRO-${deliveryId}`,
          date: Timestamp.now(), // Date d'annulation
          employeeId: userProfile.id,
          employeeName: userProfile.name,
          notes: `Annulation de l'approvisionnement ${delivery.supplierName}`,
          supplierId: delivery.supplierId,
          supplierName: delivery.supplierName,
          deliveryId: deliveryId,
          createdAt: Timestamp.now(),
          ...(delivery.siteId && delivery.siteName ? { siteId: delivery.siteId, siteName: delivery.siteName } : {}),
        };

        // Ajouter les champs TVA si présents
        if (item.taxRateId) {
          stockMovement.taxRateId = item.taxRateId;
          stockMovement.taxRateName = item.taxRateName;
          stockMovement.taxRate = item.taxRate;
          stockMovement.unitCostExcludingTax = item.unitCostExcludingTax;
          stockMovement.unitCostIncludingTax = item.unitCostIncludingTax;
          stockMovement.taxAmount = item.taxAmount;
          stockMovement.totalTaxAmount = item.totalTaxAmount;
          stockMovement.totalCostExcludingTax = item.totalCostExcludingTax;
          stockMovement.totalCostIncludingTax = item.totalCostIncludingTax;
        }

        batch.set(movementRef, stripUndefined(stockMovement) as Record<string, unknown>);
      }

      await batch.commit();
      await Promise.all(
        notificationPayloads.map((payload) =>
          createStockNotification.mutateAsync(payload).catch((error) => {
            console.warn('Erreur lors de la synchronisation de la notification de stock:', error);
          })
        )
      );

      return {
        success: true,
        deliveryId,
        productsCount: delivery.items.length,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['suppliers', businessId] });
    },
  });
};